import React, { useEffect, useState, useCallback } from "react";
import { apiClient } from "../lib/api";
import { toast } from "sonner";
import { Plus, Flag, Trash, CloudArrowDown, Archive, Users, MagnifyingGlass, Crown, ShieldCheck, CheckCircle, EnvelopeSimple } from "@phosphor-icons/react";
import UserAvatar from "../components/UserAvatar";
import AdminHealthTile from "../components/AdminHealthTile";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } };

export default function Admin() {
  const [fixtures, setFixtures] = useState([]);
  const [form, setForm] = useState({ home_team: "", away_team: "", kickoff: "", matchweek: 1 });
  const [results, setResults] = useState({});
  const [syncCfg, setSyncCfg] = useState({ season: 2024, mode: "both" });
  const [isSyncing, setIsSyncing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);  // {user, predictions} for the modal
  const [loadingViewingUser, setLoadingViewingUser] = useState(false);
  // Sort state for the user-picks modal
  const [pickSortBy, setPickSortBy] = useState("kickoff");  // kickoff | matchweek | fixture | pick | actual | points
  const [pickSortDir, setPickSortDir] = useState("asc");
  const togglePickSort = (col) => {
    if (pickSortBy === col) {
      setPickSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setPickSortBy(col);
      setPickSortDir(col === "kickoff" || col === "matchweek" || col === "fixture" ? "asc" : "desc");
    }
  };

  const openUserPicks = async (u) => {
    setLoadingViewingUser(true);
    setViewingUser({ user: u, predictions: null });
    try {
      const { data } = await apiClient.get(`/admin/users/${u.id}/predictions`);
      setViewingUser(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load picks");
      setViewingUser(null);
    } finally {
      setLoadingViewingUser(false);
    }
  };
  const [seasonLabel, setSeasonLabel] = useState("");
  // Entrants table state
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [sortBy, setSortBy] = useState("created_at"); // created_at | total_points | predictions | exact_scores | username
  const [sortDir, setSortDir] = useState("desc");

  const load = useCallback(async () => {
    const { data } = await apiClient.get("/fixtures", { params: { all: true } });
    setFixtures(data.fixtures);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiClient.get("/admin/settings")
      .then(({ data }) => setCurrentSeason(data.current_season))
      .catch(() => {});
  }, []);

  const loadUsers = useCallback(async (query) => {
    setUsersLoading(true);
    try {
      const params = query && query.trim() ? { q: query.trim() } : {};
      const { data } = await apiClient.get("/admin/users", { params });
      setUsers(data.users);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load entrants");
    } finally {
      setUsersLoading(false);
    }
  }, []);
  useEffect(() => { loadUsers(""); }, [loadUsers]);

  // Debounce server search
  useEffect(() => {
    const t = setTimeout(() => loadUsers(userQuery), 250);
    return () => clearTimeout(t);
  }, [userQuery, loadUsers]);

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.username || u.email}? This cascades all their predictions and league memberships.`)) return;
    try {
      const { data } = await apiClient.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.username || u.email} (${data.predictions_deleted} picks removed)`);
      loadUsers(userQuery);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete user");
    }
  };

  const verifyUser = async (u) => {
    if (!window.confirm(`Manually mark ${u.username || u.email} as verified? Use this when they can't receive the verification email.`)) return;
    try {
      await apiClient.post(`/admin/users/${u.id}/verify`);
      toast.success(`${u.username || u.email} is now verified`);
      loadUsers(userQuery);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to verify user");
    }
  };

  const resendVerification = async (u) => {
    try {
      const { data } = await apiClient.post(`/admin/users/${u.id}/resend-verification`);
      if (data.already_verified) toast.info(`${u.username || u.email} is already verified`);
      else if (data.sent) toast.success(`Verification email re-sent to ${u.email}`);
      else toast.error(`Could not send email to ${u.email} — check SMTP config`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend verification");
    }
  };

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir(col === "username" || col === "email" ? "asc" : "desc");
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    let va = a[sortBy], vb = b[sortBy];
    if (typeof va === "string") { va = va.toLowerCase(); vb = (vb || "").toLowerCase(); }
    if (va == null) va = "";
    if (vb == null) vb = "";
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const closeSeason = async () => {
    const label = seasonLabel.trim() || "current season";
    const confirmText = `Close ${label}? This will:\n• Snapshot the leaderboard as a permanent archive\n• Reset every user's total points to 0\n• Delete all predictions\n• Archive all current fixtures\n\nType YES to confirm.`;
    const entered = window.prompt(confirmText);
    if (entered !== "YES") {
      if (entered != null) toast.error("Cancelled — type YES exactly to confirm");
      return;
    }
    setClosing(true);
    try {
      const { data } = await apiClient.post("/admin/season/close", {
        label: seasonLabel.trim() || null,
      });
      toast.success(
        `${data.archive.label} archived · winner ${data.archive.winner.name} (${data.archive.winner.total_points} pts). Reset ${data.users_reset} users, cleared ${data.predictions_cleared} picks, archived ${data.fixtures_archived} fixtures.`
      );
      setSeasonLabel("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to close season");
    } finally {
      setClosing(false);
    }
  };

  const runCleanup = async () => {
    if (!window.confirm("Run pre-season cleanup? This deletes pre-2026 fixtures, old predictions and resets all user points to 0. Users and leagues are kept.")) return;
    setCleaning(true);
    try {
      const { data } = await apiClient.post("/admin/cleanup/pre-season");
      const r = data.report || {};
      toast.success(
        `Cleanup done · ${r.fixtures_deleted || 0} fixtures removed, ${r.users_points_reset || 0} users reset to 0 pts`
      );
      load();
      loadUsers(userQuery);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  const runSync = async () => {
    setIsSyncing(true);
    try {
      const { data } = await apiClient.post("/admin/fixtures/sync", {
        season: parseInt(syncCfg.season, 10),
        mode: syncCfg.mode,
      });
      const fmt = (r) => r ? `+${r.added || 0} added · ${r.updated || 0} updated · ${r.finished_synced || 0} newly finished · ${r.predictions_scored || 0} preds scored` : "no data";
      const summary = data.upcoming
        ? `upcoming: ${fmt(data.upcoming)} | finished: ${fmt(data.finished)}`
        : fmt(data);
      toast.success(`Synced ${data.season || syncCfg.season}: ${summary}`, { duration: 10000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const runRescore = async () => {
    if (!window.confirm("Rescore every prediction and rebuild every user's total_points from scratch? Safe to run — idempotent — but takes a few seconds.")) return;
    setIsSyncing(true);
    try {
      const { data } = await apiClient.post("/admin/rescore-all");
      toast.success(
        `Rescored: ${data.predictions_rescored} preds · ${data.predictions_changed} changed · ${data.users_updated}/${data.users_recomputed} users refreshed`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rescore failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const seedVirtual = async () => {
    const n = parseInt(window.prompt("How many virtual entrants to seed?", "10") || "0", 10);
    if (!n || n < 1) return;
    setIsSyncing(true);
    try {
      const { data } = await apiClient.post(`/admin/virtual-users/seed?count=${n}`);
      toast.success(`Created ${data.created} virtual entrants across ${data.fixtures_covered} fixtures`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const purgeVirtual = async () => {
    if (!window.confirm("Delete ALL virtual entrants + their predictions? Real users are unaffected.")) return;
    setIsSyncing(true);
    try {
      const { data } = await apiClient.delete("/admin/virtual-users");
      toast.success(`Removed ${data.deleted_users} virtual entrants · ${data.deleted_predictions} predictions`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const purgePriorSeasons = async () => {
    const cutoffStr = window.prompt(
      "Cutoff year: any fixture with kickoff BEFORE {year}-07-01 (and no matching season tag) gets purged. Leave blank to use current season.",
      String(currentSeason || 2026)
    );
    if (cutoffStr === null) return;
    const cutoff = parseInt(cutoffStr, 10);
    if (!window.confirm(`Purge every fixture with kickoff before ${cutoff}-07-01 (plus all predictions), then rebuild user totals. Continue?`)) return;
    setIsSyncing(true);
    try {
      const qs = Number.isFinite(cutoff) ? `?cutoff_year=${cutoff}&target_season=${cutoff}` : "";
      const { data } = await apiClient.post(`/admin/fixtures/purge-prior-seasons${qs}`);
      const summary = Object.entries(data.seasons_removed || {})
        .map(([k, v]) => `${k}: ${v}`).join(", ") || "none";
      toast.success(`Removed ${data.removed_fixtures} fixtures + ${data.removed_predictions} preds · ${data.users_rebalanced} users rebalanced (${summary})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const saveCurrentSeason = async () => {
    const yr = parseInt(window.prompt("Set the current EPL season year (e.g. 2026):", String(currentSeason || 2026)) || "", 10);
    if (!Number.isFinite(yr)) return;
    setIsSyncing(true);
    try {
      const { data } = await apiClient.post(`/admin/settings/current-season?year=${yr}`);
      setCurrentSeason(data.current_season);
      toast.success(`Current season set to ${data.current_season}. Seed/purge/diag will use this immediately.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const unarchiveAll = async () => {
    if (!window.confirm("Un-archive EVERY fixture — restores the fixture list if it's mysteriously empty. Safe to run always.")) return;
    setIsSyncing(true);
    try {
      const { data } = await apiClient.post("/admin/fixtures/unarchive-all");
      toast.success(`Un-archived ${data.unarchived} fixtures`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Un-archive failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const backfillSnapshots = async () => {
    if (!window.confirm("Backfill end-of-matchweek rank snapshots for the current season? Populates Previous/Highest columns on every leaderboard. Idempotent — safe to run anytime.")) return;
    setIsSyncing(true);
    try {
      const { data } = await apiClient.post("/admin/snapshots/backfill");
      const mws = (data.completed_matchweeks || []).join(", ") || "none";
      toast.success(`Snapshots created: ${data.created} · skipped: ${data.skipped} · MWs: ${mws}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const assignBotPersonalities = async () => {
    setIsSyncing(true);
    try {
      const { data } = await apiClient.post("/admin/virtual-users/assign-personalities");
      if (data.assigned === 0) {
        toast.info("All bots already have a personality assigned");
      } else {
        toast.success(`Assigned personalities to ${data.assigned} existing bots (${(data.personalities || []).join(", ")})`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const sendWeeklyDigest = async () => {
    if (!window.confirm("Send the weekly rank-movement digest email to every eligible user right now? Idempotent per ISO week — safe to run once a week.")) return;
    setIsSyncing(true);
    try {
      const { data } = await apiClient.post("/admin/email/weekly-digest");
      toast.success(`Rank digest fired · week ${data.week} · sent ${data.emails_sent} · users ${data.users ?? "—"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest send failed");
    } finally {
      setIsSyncing(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      const kickoffIso = new Date(form.kickoff).toISOString();
      await apiClient.post("/admin/fixtures", { ...form, kickoff: kickoffIso, matchweek: parseInt(form.matchweek, 10) || 1 });
      toast.success("Fixture added");
      setForm({ home_team: "", away_team: "", kickoff: "", matchweek: form.matchweek });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const saveResult = async (fid) => {
    const r = results[fid];
    if (r?.h == null || r?.a == null || r.h === "" || r.a === "") { toast.error("Enter both scores"); return; }
    try {
      await apiClient.post(`/admin/fixtures/${fid}/result`, { home_score: parseInt(r.h, 10), away_score: parseInt(r.a, 10) });
      toast.success("Result saved & predictions scored");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const del = async (fid) => {
    if (!window.confirm("Delete fixture?")) return;
    await apiClient.delete(`/admin/fixtures/${fid}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <span className="text-xs uppercase tracking-[0.25em] text-primary-yellow font-bold">Operator</span>
      <h1 className="font-heading text-5xl uppercase font-black mt-3">Admin Console</h1>

      <AdminHealthTile />

      <section className="mt-10 surface border border-white/10 p-6" data-testid="admin-entrants">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-heading text-2xl uppercase font-black flex items-center gap-2">
            <Users weight="bold" /> Entrants
            <span className="text-sm font-mono-num text-white/40 font-normal ml-2">{users.length}</span>
          </h2>
          <div className="relative w-full sm:w-80">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              data-testid="admin-users-search"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              placeholder="Search by email, username or name…"
              className="w-full bg-[#0B0D11] border border-white/10 pl-10 pr-4 py-2.5 focus:border-primary-yellow focus:outline-none text-sm"
            />
          </div>
        </div>

        <div className="mt-5 overflow-x-auto border border-white/5">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-[#0B0D11] text-[10px] uppercase tracking-[0.18em] text-white/40">
              <tr>
                <Th label="Predictor" col="username" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <Th label="Email" col="email" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <Th label="Favourite" col="favourite_team" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <Th label="Tier" col="is_premier" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <Th label="Points" col="total_points" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} num />
                <Th label="Picks" col="predictions" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} num />
                <Th label="Exact" col="exact_scores" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} num />
                <Th label="Verified" col="email_verified" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <Th label="Joined" col="created_at" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
                <th className="px-3 py-3 text-right">·</th>
              </tr>
            </thead>
            <tbody className="font-mono-num">
              {usersLoading && (
                <tr><td colSpan={10} className="px-3 py-6 text-center text-white/40 text-xs uppercase tracking-widest">Loading…</td></tr>
              )}
              {!usersLoading && sortedUsers.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-6 text-center text-white/40 text-xs uppercase tracking-widest">No entrants{userQuery ? " match this search" : " yet"}</td></tr>
              )}
              {!usersLoading && sortedUsers.map((u) => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-3 py-3">
                    <div className="font-bold text-white flex items-center gap-2">
                      <UserAvatar avatar={u.avatar} size={26} />
                      {u.is_admin && <ShieldCheck size={12} weight="fill" className="text-primary-yellow" title="Admin" />}
                      <button
                        data-testid={`admin-user-view-${u.id}`}
                        onClick={() => openUserPicks(u)}
                        className="hover:text-primary-yellow underline-offset-4 hover:underline text-left"
                        title="View this user's picks & results"
                      >
                        {u.username || "—"}
                      </button>
                      {u.is_virtual && (
                        <span
                          data-testid={`admin-user-virtual-${u.id}`}
                          className="px-1.5 py-0.5 text-[9px] uppercase tracking-widest bg-purple-500/15 text-purple-300 font-bold border border-purple-500/30"
                          title="Bot / seeded account — invisible to end users"
                        >
                          Virtual
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-white/40 font-sans pl-9">{u.name || ""}</div>
                  </td>
                  <td className="px-3 py-3 text-white/70 font-sans break-all max-w-[220px]">{u.email}</td>
                  <td className="px-3 py-3 text-white/50 font-sans text-xs">{u.favourite_team || "—"}</td>
                  <td className="px-3 py-3">
                    {u.is_admin ? (
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-primary-yellow/15 text-primary-yellow font-bold border border-primary-yellow/30">Admin</span>
                    ) : u.is_premier ? (
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-primary-yellow/15 text-primary-yellow font-bold border border-primary-yellow/30 inline-flex items-center gap-1"><Crown size={10} weight="fill" /> Premier</span>
                    ) : (
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-widest bg-white/5 text-white/40 border border-white/10">Free</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right font-bold text-primary-yellow">{u.total_points}</td>
                  <td className="px-3 py-3 text-right text-white/70">{u.predictions}</td>
                  <td className="px-3 py-3 text-right text-white/70">{u.exact_scores}</td>
                  <td className="px-3 py-3 text-xs">
                    {u.email_verified ? (
                      <span className="text-green-400/80" title="Verified">✓</span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="text-red-400/60" title="Not verified">✗</span>
                        <button
                          data-testid={`admin-user-verify-${u.id}`}
                          onClick={() => verifyUser(u)}
                          className="border border-green-500/40 text-green-400 hover:bg-green-500/10 p-1"
                          title="Mark as verified"
                        >
                          <CheckCircle size={12} weight="bold" />
                        </button>
                        <button
                          data-testid={`admin-user-resend-${u.id}`}
                          onClick={() => resendVerification(u)}
                          className="border border-primary-yellow/40 text-primary-yellow hover:bg-primary-yellow/10 p-1"
                          title="Resend verification email"
                        >
                          <EnvelopeSimple size={12} weight="bold" />
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-white/40 font-sans text-xs whitespace-nowrap">{u.created_at ? fmtDay(u.created_at) : "—"}</td>
                  <td className="px-3 py-3 text-right">
                    {!u.is_admin && (
                      <button
                        data-testid={`admin-user-delete-${u.id}`}
                        onClick={() => deleteUser(u)}
                        className="border border-red-500/40 text-red-400 hover:bg-red-500/10 p-1.5"
                        title="Delete user"
                      >
                        <Trash size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-10 surface border border-amber-500/30 p-6" data-testid="admin-cleanup">
        <h2 className="font-heading text-2xl uppercase font-black flex items-center gap-2">
          <CloudArrowDown weight="bold" className="text-amber-400" /> Pre-Season Cleanup
        </h2>
        <p className="text-white/50 text-sm mt-2">
          One-shot tidy-up for the 2026/27 launch: deletes pre-Aug 2026 fixtures, old demo fixtures, attached predictions and stale email tokens. Resets every user&apos;s points to 0 and clears favourite-team for relegated clubs. <strong className="text-white/70">Users + leagues are kept.</strong>
        </p>
        <button
          data-testid="admin-cleanup-btn"
          onClick={runCleanup}
          disabled={cleaning}
          className="mt-4 bg-amber-500/10 border border-amber-500/40 text-amber-300 font-bold uppercase tracking-[0.2em] text-xs hover:bg-amber-500/20 transition disabled:opacity-50 px-6 py-3 flex items-center gap-2"
        >
          <CloudArrowDown weight="bold" /> {cleaning ? "Cleaning…" : "Run Pre-Season Cleanup"}
        </button>
      </section>

      <section className="mt-10 surface border border-red-500/20 p-6">
        <h2 className="font-heading text-2xl uppercase font-black flex items-center gap-2">
          <Archive weight="bold" className="text-red-400" /> Close Current Season
        </h2>
        <p className="text-white/50 text-sm mt-2">
          Archives the final leaderboard, resets all user points, deletes predictions and archives every current fixture. Past archives stay accessible at <code className="text-primary-yellow">/api/seasons</code>.
        </p>
        <div className="mt-4 grid md:grid-cols-3 gap-3">
          <input
            data-testid="admin-season-label"
            value={seasonLabel}
            onChange={(e) => setSeasonLabel(e.target.value)}
            placeholder='Season label e.g. "2024/25"'
            maxLength={60}
            className="md:col-span-2 bg-[#0B0D11] border border-white/10 px-3 py-3 focus:border-primary-yellow focus:outline-none"
          />
          <button
            data-testid="admin-season-close"
            onClick={closeSeason}
            disabled={closing}
            className="bg-red-500/10 border border-red-500/40 text-red-300 font-bold uppercase tracking-[0.2em] text-xs hover:bg-red-500/20 transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Archive weight="bold" /> {closing ? "Closing…" : "Close Season"}
          </button>
        </div>
      </section>

      <section className="mt-10 surface border border-white/10 p-6">
        <h2 className="font-heading text-2xl uppercase font-black flex items-center gap-2">
          <CloudArrowDown weight="bold" /> Sync from API-Sports
        </h2>
        <p className="text-white/50 text-sm mt-2">Pulls EPL fixtures (league 39). Update the year when API-Sports releases the next season.</p>
        <div className="mt-4 grid md:grid-cols-4 gap-3" data-testid="admin-sync-panel">
          <select
            data-testid="admin-sync-mode"
            value={syncCfg.mode}
            onChange={(e) => setSyncCfg({ ...syncCfg, mode: e.target.value })}
            className="bg-[#0B0D11] border border-white/10 px-3 py-3 focus:border-primary-yellow focus:outline-none uppercase tracking-widest text-xs"
          >
            <option value="both">Both (upcoming + finished)</option>
            <option value="upcoming">Upcoming only (NS)</option>
            <option value="finished">Finished only (FT-AET-PEN)</option>
            <option value="all">All statuses</option>
          </select>
          <input
            data-testid="admin-sync-season"
            type="number"
            min="2022" max="2026"
            value={syncCfg.season}
            onChange={(e) => setSyncCfg({ ...syncCfg, season: e.target.value })}
            className="bg-[#0B0D11] border border-white/10 px-3 py-3 focus:border-primary-yellow focus:outline-none font-mono-num"
            placeholder="Season"
          />
          <button
            data-testid="admin-sync-run"
            onClick={runSync}
            disabled={isSyncing}
            className="md:col-span-2 bg-primary-yellow text-black font-bold uppercase tracking-[0.2em] text-xs hover:-translate-y-0.5 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <CloudArrowDown weight="bold" /> {isSyncing ? "Syncing…" : "Sync Now"}
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-white/5 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-sm uppercase font-black">Leaderboard looks stale?</h3>
            <p className="text-xs text-white/50 mt-1 leading-relaxed">
              Re-runs scoring across every finished fixture and rebuilds every user&apos;s total_points from their scored predictions. Idempotent.
            </p>
          </div>
          <button
            data-testid="admin-rescore-run"
            onClick={runRescore}
            disabled={isSyncing}
            className="shrink-0 border border-primary-yellow/50 text-primary-yellow uppercase tracking-[0.2em] text-xs font-bold px-4 py-3 hover:bg-primary-yellow/10 disabled:opacity-50"
          >
            Rescore All
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-white/5 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-sm uppercase font-black">Virtual entrants</h3>
            <p className="text-xs text-white/50 mt-1 leading-relaxed">
              Seed bot accounts with random teams and predictions to populate the leaderboard. Marked with a purple &quot;VIRTUAL&quot; chip — only visible to admins.
            </p>
          </div>
          <div className="shrink-0 flex gap-2">
            <button
              data-testid="admin-seed-virtual"
              onClick={seedVirtual}
              disabled={isSyncing}
              className="border border-purple-400/50 text-purple-300 uppercase tracking-[0.2em] text-xs font-bold px-3 py-3 hover:bg-purple-500/10 disabled:opacity-50"
            >
              Seed 10 Bots
            </button>
            <button
              data-testid="admin-assign-personalities"
              onClick={assignBotPersonalities}
              disabled={isSyncing}
              className="border border-purple-400/50 text-purple-300 uppercase tracking-[0.2em] text-xs font-bold px-3 py-3 hover:bg-purple-500/10 disabled:opacity-50"
              title="Backfill bot_personality on legacy bots"
            >
              Assign Personalities
            </button>
            <button
              data-testid="admin-purge-virtual"
              onClick={purgeVirtual}
              disabled={isSyncing}
              className="border border-red-400/50 text-red-300 uppercase tracking-[0.2em] text-xs font-bold px-3 py-3 hover:bg-red-500/10 disabled:opacity-50"
            >
              Purge
            </button>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-white/5 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-sm uppercase font-black">Rank snapshots</h3>
            <p className="text-xs text-white/50 mt-1 leading-relaxed">
              Backfill end-of-matchweek rank snapshots for the current season, and manually fire the weekly rank-movement digest email. Both are idempotent — safe to run repeatedly.
            </p>
          </div>
          <div className="shrink-0 flex flex-wrap gap-2">
            <button
              data-testid="admin-backfill-snapshots"
              onClick={backfillSnapshots}
              disabled={isSyncing}
              className="border border-primary-yellow/50 text-primary-yellow uppercase tracking-[0.2em] text-xs font-bold px-4 py-3 hover:bg-primary-yellow/10 disabled:opacity-50"
            >
              Backfill Snapshots
            </button>
            <button
              data-testid="admin-send-rank-digest"
              onClick={sendWeeklyDigest}
              disabled={isSyncing}
              className="border border-emerald-400/50 text-emerald-300 uppercase tracking-[0.2em] text-xs font-bold px-4 py-3 hover:bg-emerald-500/10 disabled:opacity-50"
            >
              Send Rank Digest
            </button>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-white/5 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-sm uppercase font-black">Prior-season cleanup</h3>
            <p className="text-xs text-white/50 mt-1 leading-relaxed">
              Removes every fixture whose season is set and does NOT match the current season, plus all its predictions. Rebuilds user totals afterwards. Run this once if your leaderboard is inflated by last year&apos;s data.
            </p>
          </div>
          <button
            data-testid="admin-purge-prior-seasons"
            onClick={purgePriorSeasons}
            disabled={isSyncing}
            className="shrink-0 border border-red-400/50 text-red-300 uppercase tracking-[0.2em] text-xs font-bold px-4 py-3 hover:bg-red-500/10 disabled:opacity-50"
          >
            Purge Prior Seasons
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-white/5 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-sm uppercase font-black">Current season</h3>
            <p className="text-xs text-white/50 mt-1 leading-relaxed">
              Runtime override for the active EPL season year. Seed / purge / diag use this immediately, no redeploy needed. Currently: <span className="text-primary-yellow font-mono-num font-bold" data-testid="current-season-value">{currentSeason ?? "…"}</span>
            </p>
          </div>
          <button
            data-testid="admin-set-current-season"
            onClick={saveCurrentSeason}
            disabled={isSyncing}
            className="shrink-0 border border-primary-yellow/50 text-primary-yellow uppercase tracking-[0.2em] text-xs font-bold px-4 py-3 hover:bg-primary-yellow/10 disabled:opacity-50"
          >
            Set Current Season
          </button>
        </div>
        <div className="mt-4 pt-4 border-t border-white/5 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-sm uppercase font-black">Fixture list empty?</h3>
            <p className="text-xs text-white/50 mt-1 leading-relaxed">
              Un-archives every fixture. Run if the fixtures page or picks list is blank on preview or production.
            </p>
          </div>
          <button
            data-testid="admin-unarchive-all"
            onClick={unarchiveAll}
            disabled={isSyncing}
            className="shrink-0 border border-primary-yellow/50 text-primary-yellow uppercase tracking-[0.2em] text-xs font-bold px-4 py-3 hover:bg-primary-yellow/10 disabled:opacity-50"
          >
            Un-Archive All
          </button>
        </div>
      </section>

      <section className="mt-10 surface border border-white/10 p-6">
        <h2 className="font-heading text-2xl uppercase font-black flex items-center gap-2">
          <Plus weight="bold" /> Add Fixture (manual)
        </h2>
        <form onSubmit={create} className="mt-6 grid md:grid-cols-5 gap-3" data-testid="admin-add-fixture">
          <input data-testid="admin-home" required placeholder="Home team" value={form.home_team} onChange={(e) => setForm({ ...form, home_team: e.target.value })} className="bg-[#0B0D11] border border-white/10 px-3 py-3 focus:border-primary-yellow focus:outline-none" />
          <input data-testid="admin-away" required placeholder="Away team" value={form.away_team} onChange={(e) => setForm({ ...form, away_team: e.target.value })} className="bg-[#0B0D11] border border-white/10 px-3 py-3 focus:border-primary-yellow focus:outline-none" />
          <input data-testid="admin-kickoff" required type="datetime-local" value={form.kickoff} onChange={(e) => setForm({ ...form, kickoff: e.target.value })} className="bg-[#0B0D11] border border-white/10 px-3 py-3 focus:border-primary-yellow focus:outline-none font-mono-num" />
          <input data-testid="admin-mw" required type="number" inputMode="numeric" pattern="[0-9]*" min="1" max="38" placeholder="MW" value={form.matchweek} onChange={(e) => setForm({ ...form, matchweek: e.target.value })} className="bg-[#0B0D11] border border-white/10 px-3 py-3 focus:border-primary-yellow focus:outline-none font-mono-num" />
          <button data-testid="admin-create-fixture" type="submit" className="bg-primary-yellow text-black font-bold uppercase tracking-[0.2em] text-xs hover:-translate-y-0.5 transition-transform">Add</button>
        </form>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-2xl uppercase font-black flex items-center gap-2 mb-4">
          <Flag weight="bold" /> Fixtures & Results
        </h2>
        <div className="border border-white/10">
          {[...fixtures].sort((a, b) => (a.kickoff || "").localeCompare(b.kickoff || "")).map((f) => (
            <div key={f.id} data-testid={`admin-row-${f.id}`} className="grid grid-cols-12 gap-3 items-center px-4 py-4 border-b border-white/5 surface">
              <div className="col-span-12 md:col-span-2 text-sm font-mono-num text-white/60">
                MW{f.matchweek} · {fmtDate(f.kickoff)}
              </div>
              <div className="col-span-12 md:col-span-4 font-heading uppercase font-bold">
                {f.home_team} <span className="text-white/30 mx-2">vs</span> {f.away_team}
              </div>
              <div className="col-span-6 md:col-span-3">
                {f.status === "finished" ? (
                  <div className="font-mono-num text-2xl font-black text-primary-yellow">{f.home_score}-{f.away_score}</div>
                ) : (
                  <div className="flex items-center gap-2">
                    <input data-testid={`admin-res-h-${f.id}`} type="number" inputMode="numeric" pattern="[0-9]*" min="0" placeholder="H" onChange={(e) => setResults({ ...results, [f.id]: { ...results[f.id], h: e.target.value } })} className="w-14 h-12 text-center bg-[#0B0D11] border border-white/10 focus:border-primary-yellow focus:outline-none font-mono-num font-bold" />
                    <span className="text-white/30">·</span>
                    <input data-testid={`admin-res-a-${f.id}`} type="number" inputMode="numeric" pattern="[0-9]*" min="0" placeholder="A" onChange={(e) => setResults({ ...results, [f.id]: { ...results[f.id], a: e.target.value } })} className="w-14 h-12 text-center bg-[#0B0D11] border border-white/10 focus:border-primary-yellow focus:outline-none font-mono-num font-bold" />
                  </div>
                )}
              </div>
              <div className="col-span-6 md:col-span-3 flex justify-end gap-2">
                {f.status !== "finished" && (
                  <button data-testid={`admin-save-result-${f.id}`} onClick={() => saveResult(f.id)} className="bg-primary-yellow text-black font-bold uppercase tracking-widest text-[10px] px-4 py-2 hover:-translate-y-0.5 transition-transform">Set Result</button>
                )}
                <button data-testid={`admin-del-${f.id}`} onClick={() => del(f.id)} className="border border-red-500/40 text-red-400 hover:bg-red-500/10 px-3 py-2"><Trash size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </section>
      {viewingUser && (
        <div
          data-testid="user-picks-modal"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 md:p-10 overflow-y-auto"
          onClick={() => setViewingUser(null)}
        >
          <div
            className="max-w-4xl w-full bg-[#0e0f12] border border-white/10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div>
                <div className="font-heading text-lg font-black uppercase">
                  {viewingUser.user?.username || viewingUser.user?.name || "—"}
                </div>
                <div className="text-xs text-white/50 mt-0.5">
                  {viewingUser.user?.total_points ?? 0} pts · {viewingUser.predictions?.length ?? 0} picks
                </div>
              </div>
              <button
                onClick={() => setViewingUser(null)}
                className="text-white/50 hover:text-white text-2xl leading-none px-2"
                title="Close"
              >×</button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {loadingViewingUser && <div className="p-8 text-center text-white/40 text-sm">Loading…</div>}
              {!loadingViewingUser && viewingUser.predictions?.length === 0 && (
                <div className="p-8 text-center text-white/40 text-sm">No picks yet.</div>
              )}
              {viewingUser.predictions?.length > 0 && (
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-[10px] uppercase tracking-widest text-white/50 sticky top-0">
                    <tr>
                      <PickTh label="Date" col="kickoff" sortBy={pickSortBy} sortDir={pickSortDir} onSort={togglePickSort} />
                      <PickTh label="MW" col="matchweek" sortBy={pickSortBy} sortDir={pickSortDir} onSort={togglePickSort} />
                      <PickTh label="Fixture" col="fixture" sortBy={pickSortBy} sortDir={pickSortDir} onSort={togglePickSort} />
                      <PickTh label="Pick" col="pick" sortBy={pickSortBy} sortDir={pickSortDir} onSort={togglePickSort} align="center" />
                      <PickTh label="Actual" col="actual" sortBy={pickSortBy} sortDir={pickSortDir} onSort={togglePickSort} align="center" />
                      <PickTh label="Pts" col="points" sortBy={pickSortBy} sortDir={pickSortDir} onSort={togglePickSort} align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortPicks(viewingUser.predictions, pickSortBy, pickSortDir).map((p) => (
                      <tr key={p.id} className="border-t border-white/5">
                        <td className="px-3 py-2 text-white/60 font-mono-num text-xs whitespace-nowrap">
                          {p.kickoff ? fmtDate(p.kickoff) : "—"}
                        </td>
                        <td className="px-3 py-2 text-white/60 font-mono-num">{p.matchweek ?? "—"}</td>
                        <td className="px-3 py-2 text-white">
                          {p.home_team || "?"} v {p.away_team || "?"}
                          {p.default && <span className="ml-2 text-[9px] uppercase tracking-widest text-white/40 border border-white/10 px-1">AUTO</span>}
                          {p.backfilled && <span className="ml-2 text-[9px] uppercase tracking-widest text-purple-300 border border-purple-500/30 px-1">BF</span>}
                        </td>
                        <td className="px-3 py-2 text-center font-mono-num text-primary-yellow">
                          {p.home_score}-{p.away_score}
                        </td>
                        <td className="px-3 py-2 text-center font-mono-num text-white/70">
                          {p.fixture_status === "finished"
                            ? `${p.fixture_home_score}-${p.fixture_away_score}`
                            : <span className="text-white/30">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono-num">
                          {p.scored ? <span className={p.points > 0 ? "text-primary-yellow font-bold" : "text-white/40"}>+{p.points}</span> : <span className="text-white/30">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Th = ({ label, col, sortBy, sortDir, onSort, num = false }) => {
  const active = sortBy === col;
  const arrow = active ? (sortDir === "asc" ? "↑" : "↓") : "";
  return (
    <th
      onClick={() => onSort(col)}
      className={`px-3 py-3 cursor-pointer select-none hover:text-white ${num ? "text-right" : "text-left"} ${active ? "text-primary-yellow" : ""}`}
    >
      {label} {arrow}
    </th>
  );
};

const PickTh = ({ label, col, sortBy, sortDir, onSort, align = "left" }) => {
  const active = sortBy === col;
  const arrow = active ? (sortDir === "asc" ? "↑" : "↓") : "";
  const alignClass = align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
  return (
    <th
      onClick={() => onSort(col)}
      className={`px-3 py-2 cursor-pointer select-none hover:text-white ${alignClass} ${active ? "text-primary-yellow" : ""}`}
      data-testid={`pick-th-${col}`}
    >
      {label} {arrow}
    </th>
  );
};

function sortPicks(rows, by, dir) {
  const factor = dir === "asc" ? 1 : -1;
  const val = (r) => {
    switch (by) {
      case "kickoff": return r.kickoff || "";
      case "matchweek": return Number(r.matchweek || 0);
      case "fixture": return `${r.home_team || ""} v ${r.away_team || ""}`.toLowerCase();
      case "pick": return Number(r.home_score || 0) * 100 + Number(r.away_score || 0);
      case "actual":
        return r.fixture_status === "finished"
          ? Number(r.fixture_home_score || 0) * 100 + Number(r.fixture_away_score || 0)
          : -1;
      case "points": return Number(r.points || 0);
      default: return r[by] || "";
    }
  };
  return [...rows].sort((a, b) => {
    const va = val(a);
    const vb = val(b);
    if (va < vb) return -1 * factor;
    if (va > vb) return 1 * factor;
    return 0;
  });
}
